
import { Dictionary } from "../fable_modules/fable-library-js.5.13.0/MutableMap.js";
import { defaultOf, safeHash, equals } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { tail, map, head, isEmpty, empty, filter } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { List_distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { prefixFailure, simPorts } from "./SidecarClient.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { sliceSource } from "../Simulator/FastSim/PortView.js";
import { tryGetValue } from "../fable_modules/fable-library-js.5.13.0/MapUtil.js";
import { FSharpRef } from "../fable_modules/fable-library-js.5.13.0/Types.js";

let held = undefined;

/**
 * Release what is held. Memory hygiene only - correctness never needs it, because the keying
 * above already refuses stale answers.
 */
export function forget() {
    held = undefined;
}

/**
 * Start holding for a build: the carrier `fs` under session `epoch`.
 */
export function startEpoch(fs, epoch) {
    held = [fs, epoch, new Dictionary([], {
        Equals: equals,
        GetHashCode: (x) => (safeHash(x) | 0),
    })];
}

/**
 * The instances of `wanted` this build has not yet been asked about.
 */
export function missingOf(wanted) {
    if (held != null) {
        const slices = held[2];
        return filter((i) => !slices.has(i), List_distinct(wanted, {
            Equals: equals,
            GetHashCode: (x) => (safeHash(x) | 0),
        }));
    }
    else {
        return empty();
    }
}

/**
 * Fetch and hold the slices of some instances, sequentially in one promise - a slice is a few
 * hundred bytes and a round trip a fifth of a millisecond, so even a whole design is tens of
 * milliseconds, and the selector's ask is a handful. Answers landing for a superseded build are
 * dropped by the epoch check at store time.
 */
export function fetch$(epoch, instances) {
    const go = (remaining, fetched) => {
        if (!isEmpty(remaining)) {
            const instance = head(remaining);
            const pr = simPorts(epoch, map((_arg) => _arg, head(remaining)));
            return pr.then((_arg_1) => {
                if (_arg_1.tag === 0) {
                    let matchResult, heldEpoch_1, slices_1;
                    if (held != null) {
                        if ((held[2], held[1] === epoch)) {
                            matchResult = 0;
                            heldEpoch_1 = held[1];
                            slices_1 = held[2];
                        }
                        else {
                            matchResult = 1;
                        }
                    }
                    else {
                        matchResult = 1;
                    }
                    switch (matchResult) {
                        case 0: {
                            slices_1.set(instance, _arg_1.fields[0]);
                            break;
                        }
                    }
                    return go(tail(remaining), fetched + 1);
                }
                else {
                    return Promise.resolve(new FSharpResult$2(/* Error */ 1, [prefixFailure(`describing ${instance}`, _arg_1.fields[0])]));
                }
            });
        }
        else {
            return Promise.resolve(new FSharpResult$2(/* Ok */ 0, [fetched]));
        }
    };
    return go(List_distinct(instances, {
        Equals: equals,
        GetHashCode: (x) => (safeHash(x) | 0),
    }), 0);
}

/**
 * Test-only: what is held and whether it is keyed to `fs`, for the dev harness.
 */
export function describeHeld(fs) {
    if (held != null) {
        const slices = held[2];
        const current = held[0] === fs;
        return `epoch ${held[1]}, ${slices.size} instances, of the current build: ${current}`;
    }
    else {
        return "nothing held";
    }
}

/**
 * Test-only: hold one instance's slice as if it had arrived on the wire.
 */
export function storeForTest(epoch, instance, slice) {
    let matchResult, heldEpoch_1, slices_1;
    if (held != null) {
        if ((held[2], held[1] === epoch)) {
            matchResult = 0;
            heldEpoch_1 = held[1];
            slices_1 = held[2];
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0: {
            slices_1.set(instance, slice);
            break;
        }
        case 1: {
            break;
        }
    }
}

sliceSource((fs, instance) => {
    let matchResult, heldFs_1, slices_1;
    if (held != null) {
        if ((held[2], held[0] === fs)) {
            matchResult = 0;
            heldFs_1 = held[0];
            slices_1 = held[2];
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0: {
            let matchValue;
            let outArg = defaultOf();
            matchValue = [tryGetValue(slices_1, instance, new FSharpRef(() => outArg, (v) => {
                outArg = v;
            })), outArg];
            return matchValue[0] ? matchValue[1] : undefined;
        }
        default:
            return undefined;
    }
});

